import { observer } from 'mobx-react';
import type { FC, ReactNode } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_COLUMNS = 2;

interface BreakpointColsObject {
  default: number;
  [breakpoint: string]: number;
}

interface MasonryProps extends React.HTMLAttributes<HTMLDivElement> {
  breakpointCols?: BreakpointColsObject | number | string;
  columnClassName?: string;
  columnAttrs?: React.HTMLAttributes<HTMLDivElement>;
  column?: React.HTMLAttributes<HTMLDivElement>;
}

const getColumnCount = (
  breakpointCols?: BreakpointColsObject | number | string
): number => {
  const windowWidth = window?.innerWidth ?? Infinity;
  let breakpointColsObject: BreakpointColsObject;

  if (typeof breakpointCols !== 'object') {
    breakpointColsObject = {
      default: parseInt(breakpointCols as string, 10) || DEFAULT_COLUMNS
    };
  } else {
    breakpointColsObject = breakpointCols;
  }

  let matchedBreakpoint = Infinity;
  let columns: number = breakpointColsObject.default || DEFAULT_COLUMNS;

  for (const breakpoint in breakpointColsObject) {
    const optBreakpoint = parseInt(breakpoint, 10);
    const isCurrentBreakpoint =
      optBreakpoint > 0 && windowWidth <= optBreakpoint;

    if (isCurrentBreakpoint && optBreakpoint < matchedBreakpoint) {
      matchedBreakpoint = optBreakpoint;
      columns = breakpointColsObject[breakpoint];
    }
  }

  return Math.max(1, columns || 1);
};

const Masonry: FC<MasonryProps> = ({
  breakpointCols,
  columnClassName,
  columnAttrs = {},
  column,
  className,
  children,
  ...rest
}) => {
  const [columnCount, setColumnCount] = useState(() =>
    getColumnCount(breakpointCols)
  );
  const animationFrameRef = useRef<number>(0);

  const reCalculateColumnCount = useCallback(() => {
    const columns = getColumnCount(breakpointCols);
    setColumnCount(prev => (prev !== columns ? columns : prev));
  }, [breakpointCols]);

  const reCalculateColumnCountDebounce = useCallback(() => {
    if (!window?.requestAnimationFrame) {
      reCalculateColumnCount();
      return;
    }

    if (window.cancelAnimationFrame) {
      window.cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = window.requestAnimationFrame(() => {
      reCalculateColumnCount();
    });
  }, [reCalculateColumnCount]);

  useEffect(() => {
    reCalculateColumnCount();

    if (window) {
      window.addEventListener('resize', reCalculateColumnCountDebounce);
    }

    return () => {
      if (window) {
        window.removeEventListener('resize', reCalculateColumnCountDebounce);
      }
    };
  }, [reCalculateColumnCount, reCalculateColumnCountDebounce]);

  useEffect(() => {
    reCalculateColumnCount();
  });

  const itemsInColumns = (): ReactNode[][] => {
    const result: ReactNode[][] = new Array(columnCount);
    const items = React.Children.toArray(children);

    for (let i = 0; i < items.length; i++) {
      const columnIndex = i % columnCount;

      if (!result[columnIndex]) {
        result[columnIndex] = [];
      }

      result[columnIndex].push(items[i]);
    }

    return result;
  };

  const renderColumns = (): ReactNode[] => {
    const childrenInColumns = itemsInColumns();
    const columnWidth = `${100 / childrenInColumns.length}%`;
    let colClassName: string | undefined = columnClassName;

    if (colClassName && typeof colClassName !== 'string') {
      console.error(
        '[Masonry]',
        'The property "columnClassName" requires a string'
      );

      if (typeof colClassName === 'undefined') {
        colClassName = 'my-masonry-grid_column';
      }
    }

    const columnAttributes = {
      ...column,
      ...columnAttrs,
      style: {
        ...columnAttrs.style,
        width: columnWidth
      },
      className: colClassName
    };

    return childrenInColumns.map((items, i) => (
      <div {...columnAttributes} key={i}>
        {items}
      </div>
    ));
  };

  let classNameOutput = className;

  if (typeof className !== 'string') {
    console.error('[Masonry]', 'The property "className" requires a string');

    if (typeof className === 'undefined') {
      classNameOutput = 'my-masonry-grid';
    }
  }

  return (
    <div {...rest} className={classNameOutput}>
      {renderColumns()}
    </div>
  );
};

export default observer(Masonry);
